import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, forwardRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const SAFE_TAGS = new Set(['P', 'BR', 'H1', 'H2', 'H3', 'H4', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE', 'UL', 'OL', 'LI', 'A', 'BLOCKQUOTE', 'HR', 'DIV']);
const SAFE_STYLES = new Set(['text-align']);

@Component({
  selector: 'app-rich-text-editor',
  standalone: true,
  imports: [CommonModule],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RichTextEditorComponent), multi: true }],
  templateUrl: './rich-text-editor.component.html',
  styleUrl: './rich-text-editor.component.css'
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('editor', { static: true }) private editorRef!: ElementRef<HTMLDivElement>;
  @Input() placeholder = 'Escribe el contenido...';
  @Input() invalid = false;
  @Input() maxLength = 20000;

  disabled = false;
  fullscreen = false;
  words = 0;
  htmlLength = 0;
  block = 'p';
  readonly active: Record<string, boolean> = {};
  readonly blocks = [
    { value: 'p', label: 'Párrafo' },
    { value: 'h2', label: 'Título' },
    { value: 'h3', label: 'Subtítulo' },
    { value: 'h4', label: 'Sección' },
    { value: 'blockquote', label: 'Cita' }
  ];

  private pending = '';
  private lastRange: Range | null = null;
  private previousBodyOverflow = '';
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly selectionListener = () => this.refreshState();

  ngAfterViewInit(): void {
    this.applyValue(this.pending);
    document.addEventListener('selectionchange', this.selectionListener);
  }

  ngOnDestroy(): void {
    document.removeEventListener('selectionchange', this.selectionListener);
    if (this.fullscreen) {
      document.body.classList.remove('policy-editor-fullscreen');
      document.body.style.overflow = this.previousBodyOverflow;
    }
  }

  writeValue(value: string | null | undefined): void {
    this.pending = value || '';
    if (this.editorRef) this.applyValue(this.pending);
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void { this.disabled = disabled; }

  exec(command: string, value?: string): void {
    if (this.disabled) return;
    this.restoreSelection();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(command, false, value);
    this.emitValue();
    this.refreshState();
  }

  setBlock(value: string): void {
    this.exec('formatBlock', value);
  }

  keepSelection(event: MouseEvent): void {
    const tag = (event.target as HTMLElement).tagName;
    if (!['SELECT', 'OPTION'].includes(tag)) event.preventDefault();
  }

  addLink(): void {
    const url = (window.prompt('Dirección del enlace (https://... o mailto:...)', 'https://') || '').trim();
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)[^\s]+$/i.test(url)) {
      window.alert('Escribe un enlace válido que comience con https://, http:// o mailto:.');
      return;
    }
    this.exec('createLink', url);
  }

  clearFormatting(): void {
    this.exec('removeFormat');
  }

  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;
    if (this.fullscreen) {
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = this.previousBodyOverflow;
    }
    document.body.classList.toggle('policy-editor-fullscreen', this.fullscreen);
    setTimeout(() => this.editorRef.nativeElement.focus());
  }

  onInput(): void {
    this.emitValue();
    this.refreshState();
  }

  onBlur(): void {
    this.onTouched();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const html = event.clipboardData?.getData('text/html');
    const text = event.clipboardData?.getData('text/plain') || '';
    const safe = html ? this.cleanHtml(html) : this.plainTextToHtml(text);
    this.restoreSelection();
    document.execCommand('insertHTML', false, safe);
    this.emitValue();
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.fullscreen) {
      event.stopPropagation();
      this.toggleFullscreen();
    }
  }

  private applyValue(html: string): void {
    const safe = this.cleanHtml(html);
    if (this.editorRef.nativeElement.innerHTML !== safe) this.editorRef.nativeElement.innerHTML = safe;
    this.refreshStats();
  }

  private emitValue(): void {
    const safe = this.cleanHtml(this.editorRef.nativeElement.innerHTML);
    const text = this.htmlToText(safe);
    const value = text ? safe : '';
    this.htmlLength = value.length;
    this.words = (text.match(/\S+/g) || []).length;
    this.onChange(value);
  }

  private refreshStats(): void {
    const html = this.cleanHtml(this.editorRef.nativeElement.innerHTML);
    const text = this.htmlToText(html);
    this.htmlLength = text ? html.length : 0;
    this.words = (text.match(/\S+/g) || []).length;
  }

  private refreshState(): void {
    const selection = document.getSelection();
    if (!selection?.anchorNode || !this.editorRef.nativeElement.contains(selection.anchorNode)) return;
    if (selection.rangeCount) this.lastRange = selection.getRangeAt(0).cloneRange();
    for (const command of ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList', 'justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull']) {
      try { this.active[command] = document.queryCommandState(command); } catch { this.active[command] = false; }
    }
    const block = String(document.queryCommandValue('formatBlock') || 'p').toLowerCase();
    this.block = this.blocks.some(option => option.value === block) ? block : 'p';
  }

  private restoreSelection(): void {
    this.editorRef.nativeElement.focus();
    if (!this.lastRange) return;
    const selection = document.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(this.lastRange);
  }

  private cleanHtml(html: string): string {
    if (!html) return '';
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const walk = (parent: Element): void => {
      for (const child of Array.from(parent.children)) {
        if (!SAFE_TAGS.has(child.tagName)) {
          if (['SCRIPT', 'STYLE', 'META', 'LINK', 'IMG', 'SVG', 'IFRAME', 'OBJECT', 'EMBED'].includes(child.tagName)) child.remove();
          else { walk(child); child.replaceWith(...Array.from(child.childNodes)); }
          continue;
        }
        const href = child.tagName === 'A' ? child.getAttribute('href') : null;
        const styles = Array.from((child as HTMLElement).style || [])
          .filter(name => SAFE_STYLES.has(name))
          .map(name => `${name}: ${(child as HTMLElement).style.getPropertyValue(name)}`);
        for (const attribute of Array.from(child.attributes)) child.removeAttribute(attribute.name);
        if (styles.length) child.setAttribute('style', styles.join('; '));
        if (href && /^(https?:\/\/|mailto:)[^\s]+$/i.test(href)) {
          child.setAttribute('href', href);
          child.setAttribute('target', '_blank');
          child.setAttribute('rel', 'noopener noreferrer');
        }
        walk(child);
      }
    };
    walk(documentFragment.body);
    return documentFragment.body.innerHTML.replace(/<!--[\s\S]*?-->/g, '').trim();
  }

  private plainTextToHtml(text: string): string {
    const escape = (value: string) => value.replace(/[&<>]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[character] || character));
    return text.split(/\n{2,}/).map(paragraph => `<p>${escape(paragraph).replace(/\n/g, '<br>')}</p>`).join('');
  }

  private htmlToText(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ').trim();
  }
}
